import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { api } from '../utils/api';

export default function CheckoutPage() {
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [pickupPoints, setPickupPoints] = useState([]);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (items.length === 0) navigate('/');
    api.get('/pickup-points').then(setPickupPoints);
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'שם חובה';
    if (!form.phone.trim() || !/^0\d{8,9}$/.test(form.phone.replace(/[-\s]/g, ''))) e.phone = 'מספר טלפון לא תקין';
    if (!selectedPickup) e.pickup = 'יש לבחור נקודת חלוקה';
    return e;
  };

  const selectedPickupData = pickupPoints.find(p => p.id === selectedPickup);
  const deliveryFee = selectedPickupData?.fee > 0 ? parseFloat(selectedPickupData.fee) : 0;
  const grandTotal = total + deliveryFee;

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSubmitting(true);
    try {
      const result = await api.post('/orders', {
        customer_name: form.name,
        customer_phone: form.phone,
        customer_email: form.email,
        pickup_point_id: selectedPickup,
        items: items.map(i => ({ id: i.id, name: i.name, quantity: i.quantity, price: i.price, sale_price: i.sale_price, product_code: i.product_code })),
        total: grandTotal,
        delivery_fee: deliveryFee,
        notes: form.notes,
      });
      if (result.success) {
        clearCart();
        navigate('/success', { state: { orderId: result.orderId } });
      }
    } catch {
      alert('אירעה שגיאה. נסה שוב.');
    } finally {
      setSubmitting(false);
    }
  };

  const set = (field) => (e) => { setForm(f => ({ ...f, [field]: e.target.value })); setErrors(err => ({ ...err, [field]: null })); };

  return (
    <div className="page">
      <h1 className="page-title">📋 סיום הזמנה</h1>
      <div className="checkout-grid">
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Customer details */}
          <div className="card-box">
            <h2>פרטי הלקוח</h2>
            <div className="form-group">
              <label className="form-label">שם מלא *</label>
              <input className="form-input" value={form.name} onChange={set('name')} placeholder="ישראל ישראלי" />
              {errors.name && <small style={{ color: 'red' }}>{errors.name}</small>}
            </div>
            <div className="form-group">
              <label className="form-label">טלפון *</label>
              <input className="form-input" value={form.phone} onChange={set('phone')} placeholder="050-0000000" dir="ltr" />
              {errors.phone && <small style={{ color: 'red' }}>{errors.phone}</small>}
            </div>
            <div className="form-group">
              <label className="form-label">אימייל (אופציונלי)</label>
              <input className="form-input" value={form.email} onChange={set('email')} placeholder="israel@gmail.com" dir="ltr" />
            </div>
            <div className="form-group">
              <label className="form-label">הערות להזמנה</label>
              <textarea className="form-textarea" value={form.notes} onChange={set('notes')} placeholder="הערות, בקשות מיוחדות..." />
            </div>
          </div>

          {/* Pickup point */}
          <div className="card-box">
            <h2>📍 נקודת חלוקה</h2>
            {errors.pickup && <small style={{ color: 'red', display: 'block', marginBottom: 10 }}>{errors.pickup}</small>}
            <div className="pickup-options">
              {pickupPoints.map(p => (
                <div
                  key={p.id}
                  className={`pickup-option ${selectedPickup === p.id ? 'selected' : ''}`}
                  onClick={() => { setSelectedPickup(p.id); setErrors(e => ({ ...e, pickup: null })); }}
                >
                  <div className="pickup-option-name">📍 {p.name} — {p.city}{p.fee > 0 ? <span style={{ marginRight: 8, fontWeight: 700, color: 'var(--primary-dark)' }}>+₪{parseFloat(p.fee).toFixed(2)}</span> : <span style={{ marginRight: 8, color: 'var(--green)', fontSize: 13 }}>חינם</span>}</div>
                  <div className="pickup-option-addr">{p.address}</div>
                  {p.notes && <div className="pickup-option-notes">🕐 {p.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right column — order summary */}
        <div>
          <div className="card-box" style={{ position: 'sticky', top: 80 }}>
            <h2>סיכום הזמנה</h2>
            {items.map(item => (
              <div key={item.id} className="order-summary-item">
                <span>{item.name} × {item.quantity}</span>
                <span>₪{((item.sale_price || item.price) * item.quantity).toFixed(2)}</span>
              </div>
            ))}
            {deliveryFee > 0 && (
              <div className="order-summary-item" style={{ color: 'var(--text-light)' }}>
                <span>🚚 דמי משלוח</span>
                <span>₪{deliveryFee.toFixed(2)}</span>
              </div>
            )}
            <div className="order-total-row">
              <span>סה"כ לתשלום</span>
              <span>₪{grandTotal.toFixed(2)}</span>
            </div>
            <p style={{ fontSize: 13, color: '#888', margin: '8px 0 0' }}>
              💡 התשלום יתבצע בנקודת החלוקה בעת קבלת ההזמנה
            </p>
            <button
              className="submit-order-btn"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'שולח...' : '✅ שלח הזמנה'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
